import { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFieldArray, useForm } from "react-hook-form";
import { showToast } from "src/componentes";
import { MapearErrosServidor } from "src/hook/Functions";
import { MDFeCIOTOutput, MDFeGerarCIOT } from "src/services/Fiscal.Service";
import { ETipoOperacaoCIOT } from "../../MDFe.Dto";
import {
  CiotGerarFormType,
  CiotGerarSchema,
  CiotValoresIniciais,
  montarPayloadCiot,
} from "./MDFe.Modal.GerarCIOT.Dto";

interface Props {
  show: boolean;
  inicial: CiotValoresIniciais | null;
  onClose: () => void;
  onGerado: (resultado: MDFeCIOTOutput) => void;
}

const primeiraMensagemErro = (erros: any): string | undefined => {
  if (!erros || typeof erros !== "object") return undefined;
  if (typeof erros.message === "string" && erros.message) return erros.message;
  for (const chave of Object.keys(erros)) {
    if (chave === "ref") continue;
    const msg = primeiraMensagemErro(erros[chave]);
    if (msg) return msg;
  }
  return undefined;
};

const MDFeModalGerarCIOTCode = ({ show, inicial, onClose, onGerado }: Props) => {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<CiotGerarFormType>({
    resolver: yupResolver(CiotGerarSchema) as any,
    defaultValues: inicial?.valores,
  });

  const veiculos = useFieldArray({ control: form.control, name: "veiculos" });
  const pagamentos = useFieldArray({ control: form.control, name: "pagamentos" });
  const contratantesFracionada = useFieldArray({
    control: form.control,
    name: "contratantesCargaFracionada",
  });

  useEffect(() => {
    if (show && inicial) {
      form.reset(inicial.valores);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, inicial]);

  const tipoOperacao = Number(form.watch("tipoOperacao"));
  const exibeCarga =
    tipoOperacao === ETipoOperacaoCIOT.CargaLotacao ||
    tipoOperacao === ETipoOperacaoCIOT.CargaFracionada;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const onInvalid = (erros: any) => {
    showToast({
      message:
        primeiraMensagemErro(erros) ??
        "Existem campos obrigatórios não preenchidos para gerar o CIOT.",
      title: "Dados do CIOT",
      variant: "Warning",
    });
  };

  const onValid = async (data: CiotGerarFormType) => {
    setSubmitting(true);
    try {
      const response = await MDFeGerarCIOT(montarPayloadCiot(data));
      if (!response.data.success) {
        MapearErrosServidor(response.data.notifications, showToast);
        return;
      }

      const resultado = response.data.data as MDFeCIOTOutput;
      if (!resultado?.ciot) {
        showToast({
          message: "A ANTT respondeu com sucesso, mas o código CIOT não foi retornado.",
          title: "Atenção",
          variant: "Warning",
        });
        return;
      }

      if (resultado.reutilizado) {
        showToast({
          message: `Já existia o CIOT ${resultado.ciot} pendente de vínculo (últimas 2 h) para esta placa e UFs. Ele foi reaproveitado e nada foi enviado à ANTT. Para gerar outro, marque "Forçar novo CIOT".`,
          title: "CIOT Reaproveitado",
          variant: "Info",
        });
      } else {
        showToast({
          message: resultado.mensagem || `CIOT ${resultado.ciot} gerado com sucesso pela ANTT!`,
          title: "CIOT Gerado",
          variant: "Success",
        });
      }

      onGerado(resultado);
    } catch (error: any) {
      const notificacoes = error?.response?.data?.notifications;
      if (Array.isArray(notificacoes)) {
        MapearErrosServidor(notificacoes, showToast);
      } else {
        showToast({
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Erro ao comunicar com a ANTT para geração do CIOT.",
          title: "Falha na Geração do CIOT",
          variant: "Danger",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGerar = form.handleSubmit(onValid, onInvalid);

  return {
    form,
    submitting,
    exibeCarga,
    tipoOperacao,
    veiculos,
    pagamentos,
    contratantesFracionada,
    handleClose,
    handleGerar,
  };
};

export default MDFeModalGerarCIOTCode;
